import { DynamoDBClient, GetItemCommand, PutItemCommand, QueryCommand, UpdateItemCommand } from '@aws-sdk/client-dynamodb';
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb';
import { SchedulingData, TimeSlot, TimeSlotVote, MeetingComment } from '../interfaces/interface';
// MeetingComment is used for per-slot comments (sk: MEETING#<id>#TIME_SLOT#<slotId>#COMMENTS)
import { EmployeeSettingsService } from './EmployeeSettingsService';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { CognitoService } from 'src/services/aws/CognitoService';
import { logger } from 'src/logger';

const stage = ENVIRONMENT_VARIABLES.env.Stage;
const normalizedStage = stage.toLowerCase() === 'dev' ? 'alpha' : stage.toLowerCase();
const TABLE_NAME = `finsuite_ea_huddle_employee_settings_${normalizedStage}`;

/**
 * Service for persisting time slot scheduling data to DynamoDB.
 *
 * Schema:
 * - pk: employee_alias (creator)
 * - sk: MEETING#<meetingId>#TIME_SLOT#<slotId> — individual time slot with votes
 * - sk: MEETING#<meetingId>#TIME_SLOT#<slotId>#COMMENTS — per-slot comments
 *
 * Scheduling metadata (duration, repeat, organizerLogin, subject) is stored
 * on the main MEETING#<meetingId> record managed by EmployeeSettingsService.
 */
export class SchedulingService {
  private static client: DynamoDBClient | null = null;

  private static getClient(): DynamoDBClient {
    if (this.client) return this.client;
    this.client = new DynamoDBClient({
      region: ENVIRONMENT_VARIABLES.env.Region,
      credentials: CognitoService.credentialProvider
    });
    return this.client;
  }

  // ── Time Slots ───────────────────────────────────────────────────────

  /**
   * Save a single time slot (creates or overwrites).
   */
  public static async saveTimeSlot(creatorAlias: string, meetingId: string, slot: TimeSlot): Promise<void> {
    const client = this.getClient();
    // Build votesMap from votes array for atomic upsert support
    const votesMap: Record<string, TimeSlotVote> = {};
    for (const v of slot.votes) {
      votesMap[v.loginName] = v;
    }
    await client.send(
      new PutItemCommand({
        TableName: TABLE_NAME,
        Item: marshall(
          {
            employee_alias: creatorAlias,
            sk: `MEETING#${meetingId}#TIME_SLOT#${slot.slotId}`,
            ...slot,
            votesMap,
            updatedAt: new Date().toISOString()
          },
          { removeUndefinedValues: true }
        )
      })
    );
  }

  /**
   * Get all time slots for a meeting (including soft-deleted for archive view).
   */
  public static async getTimeSlots(creatorAlias: string, meetingId: string): Promise<TimeSlot[]> {
    const client = this.getClient();
    const result = await client.send(
      new QueryCommand({
        TableName: TABLE_NAME,
        KeyConditionExpression: 'employee_alias = :pk AND begins_with(sk, :prefix)',
        ExpressionAttributeValues: marshall({
          ':pk': creatorAlias,
          ':prefix': `MEETING#${meetingId}#TIME_SLOT#`
        })
      })
    );

    if (!result.Items || result.Items.length === 0) return [];

    // Filter out sub-records like #COMMENTS that also match the begins_with prefix
    const timeSlotItems = result.Items.map((raw) => unmarshall(raw)).filter((item) => !(item.sk as string).endsWith('#COMMENTS'));

    return timeSlotItems
      .filter((item) => !item.isDeleted)
      .map((item) => {
        // Read from votesMap (atomic) with fallback to votes array (legacy data)
        const votesMap = item.votesMap as Record<string, TimeSlotVote> | undefined;
        const votes: TimeSlotVote[] = votesMap ? Object.values(votesMap) : ((item.votes ?? []) as TimeSlotVote[]);
        return {
          slotId: item.slotId ?? '',
          date: item.date ?? '',
          startTime: item.startTime ?? '',
          endTime: item.endTime ?? '',
          timezone: item.timezone ?? '',
          order: item.order ?? 0,
          votes
        };
      })
      .sort((a, b) => a.order - b.order);
  }

  /**
   * Soft-delete a time slot by marking it as deleted (sets isDeleted=true).
   * The time slot remains in DynamoDB but is filtered out from active queries.
   */
  public static async deleteTimeSlot(creatorAlias: string, meetingId: string, slotId: string): Promise<void> {
    const client = this.getClient();
    await client.send(
      new UpdateItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: creatorAlias,
          sk: `MEETING#${meetingId}#TIME_SLOT#${slotId}`
        }),
        UpdateExpression: 'SET isDeleted = :d',
        ExpressionAttributeValues: marshall({ ':d': true })
      })
    );
  }

  /**
   * Update the order of time slots (for reorder).
   */
  public static async updateSlotOrder(creatorAlias: string, meetingId: string, slotId: string, newOrder: number): Promise<void> {
    const client = this.getClient();
    await client.send(
      new UpdateItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: creatorAlias,
          sk: `MEETING#${meetingId}#TIME_SLOT#${slotId}`
        }),
        UpdateExpression: 'SET #ord = :order',
        ExpressionAttributeNames: { '#ord': 'order' },
        ExpressionAttributeValues: marshall({ ':order': newOrder })
      })
    );
  }

  // ── Votes ────────────────────────────────────────────────────────────

  /**
   * Add or update a vote on a time slot.
   * Uses a DynamoDB Map keyed by loginName for a single atomic upsert —
   * no read required, eliminating race conditions entirely.
   */
  public static async saveVote(creatorAlias: string, meetingId: string, slotId: string, vote: TimeSlotVote): Promise<boolean> {
    const client = this.getClient();
    try {
      await client.send(
        new UpdateItemCommand({
          TableName: TABLE_NAME,
          Key: marshall({
            employee_alias: creatorAlias,
            sk: `MEETING#${meetingId}#TIME_SLOT#${slotId}`
          }),
          UpdateExpression: 'SET votesMap.#loginName = :vote',
          ConditionExpression: 'attribute_exists(employee_alias)',
          ExpressionAttributeNames: { '#loginName': vote.loginName },
          ExpressionAttributeValues: marshall({ ':vote': vote })
        })
      );
      return true;
    } catch (error) {
      if ((error as { name: string }).name === 'ConditionalCheckFailedException') {
        logger.error('Time slot not found for vote', { meetingId, slotId });
        return false;
      }
      throw error;
    }
  }

  // ── Per-Slot Comments ────────────────────────────────────────────────

  /**
   * Get all comments for a specific time slot.
   * Stored as a single DDB record: sk=MEETING#<meetingId>#TIME_SLOT#<slotId>#COMMENTS
   */
  public static async getTimeSlotComments(creatorAlias: string, meetingId: string, slotId: string): Promise<MeetingComment[]> {
    const client = this.getClient();
    const result = await client.send(
      new GetItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: creatorAlias,
          sk: `MEETING#${meetingId}#TIME_SLOT#${slotId}#COMMENTS`
        })
      })
    );

    if (!result.Item) return [];
    const item = unmarshall(result.Item);
    return ((item.comments ?? []) as MeetingComment[]).sort(
      (a: MeetingComment, b: MeetingComment) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }

  /**
   * Save the full comments array for a time slot (overwrites).
   */
  public static async saveTimeSlotComments(creatorAlias: string, meetingId: string, slotId: string, comments: MeetingComment[]): Promise<void> {
    const client = this.getClient();
    await client.send(
      new PutItemCommand({
        TableName: TABLE_NAME,
        Item: marshall(
          {
            employee_alias: creatorAlias,
            sk: `MEETING#${meetingId}#TIME_SLOT#${slotId}#COMMENTS`,
            comments,
            updatedAt: new Date().toISOString()
          },
          { removeUndefinedValues: true }
        )
      })
    );
  }

  /**
   * Add a comment (top-level or reply) to a time slot.
   * Uses atomic list_append to avoid race conditions when multiple users
   * comment concurrently.
   */
  public static async addTimeSlotComment(creatorAlias: string, meetingId: string, slotId: string, comment: MeetingComment): Promise<void> {
    const client = this.getClient();
    await client.send(
      new UpdateItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: creatorAlias,
          sk: `MEETING#${meetingId}#TIME_SLOT#${slotId}#COMMENTS`
        }),
        UpdateExpression: 'SET comments = list_append(if_not_exists(comments, :empty), :newComment), updatedAt = :now',
        ExpressionAttributeValues: marshall(
          { ':newComment': [comment], ':empty': [], ':now': new Date().toISOString() },
          { removeUndefinedValues: true }
        )
      })
    );
  }

  /**
   * Edit a time slot comment. Only the author should call this.
   */
  public static async editTimeSlotComment(
    creatorAlias: string,
    meetingId: string,
    slotId: string,
    commentId: string,
    newContent: string
  ): Promise<void> {
    const comments = await this.getTimeSlotComments(creatorAlias, meetingId, slotId);
    const comment = comments.find((c) => c.commentId === commentId);
    if (!comment) {
      logger.error('Time slot comment not found', { meetingId, slotId, commentId });
      return;
    }
    comment.content = newContent;
    comment.updatedAt = new Date().toISOString();
    await this.saveTimeSlotComments(creatorAlias, meetingId, slotId, comments);
  }

  // ── Full Scheduling Data Load ────────────────────────────────────────

  /**
   * Load complete scheduling data for a meeting.
   * Reads metadata (duration, repeat, organizerLogin) from the main meeting record
   * and time slots + comments from their own DDB records.
   */
  public static async getFullSchedulingData(creatorAlias: string, meetingId: string): Promise<SchedulingData | null> {
    const [meetingData, timeSlots] = await Promise.all([
      EmployeeSettingsService.getMeeting(creatorAlias, meetingId),
      this.getTimeSlots(creatorAlias, meetingId)
    ]);

    return {
      meetingId,
      organizerLogin: meetingData?.organizerLogin ?? creatorAlias,
      duration: meetingData?.duration ?? 30,
      repeat: meetingData?.repeat ?? 'none',
      timeSlots
    };
  }
}
